/**
 * GSAP Handler
 *
 * Centralized GSAP detection, error handling, and utilities.
 * Provides type-safe access to GSAP, ScrollTrigger, and Draggable.
 *
 * Features:
 * - Global scroll velocity tracking
 * - ScrollTrigger refresh on image load
 * - Prefers-reduced-motion detection
 */

import { waitForIx3 } from './webflow.ts';

// Type definitions for externally loaded GSAP
export interface GsapTween {
  kill: () => void;
  then: () => Promise<void>;
}

export interface GsapTimeline {
  to: (target: unknown, vars: Record<string, unknown>, position?: number | string) => GsapTimeline;
  from: (
    target: unknown,
    vars: Record<string, unknown>,
    position?: number | string
  ) => GsapTimeline;
  set: (target: unknown, vars: Record<string, unknown>, position?: number | string) => GsapTimeline;
  call: (callback: () => void, params?: unknown[], position?: number | string) => GsapTimeline;
  play: () => GsapTimeline;
  pause: () => GsapTimeline;
  kill: () => void;
  timeScale: (value: number) => GsapTimeline;
}

interface GsapPlugin {
  create: (config: unknown) => void;
  getAll: () => unknown[];
  refresh: () => void;
}

interface GsapDraggable {
  create: (target: unknown, vars?: Record<string, unknown>) => unknown[];
}

interface GsapInstance {
  to: (target: unknown, vars: Record<string, unknown>) => GsapTween;
  from: (target: unknown, vars: Record<string, unknown>) => GsapTween;
  set: (target: unknown, vars: Record<string, unknown>) => void;
  timeline: (vars?: Record<string, unknown>) => GsapTimeline;
  registerPlugin: (...plugins: unknown[]) => void;
  ticker: {
    add: (callback: () => void) => void;
    remove: (callback: () => void) => void;
  };
}

// Declare global types for GSAP (loaded externally)
declare global {
  interface Window {
    gsap?: GsapInstance;
    ScrollTrigger?: GsapPlugin;
    Draggable?: GsapDraggable;
  }
}

let hasWarned = false;
let globalScrollVelocity = 0;
let globalScrollDirection = 0; // 1 = down, -1 = up, 0 = not scrolling
let scrollVelocityInitialized = false;
let scrollTriggerRefreshInitialized = false;

/**
 * Initialize global scroll velocity tracking
 */
export function initScrollVelocityTracking(): void {
  if (scrollVelocityInitialized || !hasScrollTrigger()) {
    return;
  }

  const gsap = window.gsap!;

  // Track scroll position and smooth velocity
  let lastScrollY = window.scrollY;
  let lastTime = performance.now();
  let targetVelocity = 0;
  let currentVelocity = 0;

  // Update target velocity and direction on scroll
  window.ScrollTrigger!.create({
    onUpdate: () => {
      const currentScrollY = window.scrollY;
      const currentTime = performance.now();
      const deltaY = currentScrollY - lastScrollY;
      const deltaTime = currentTime - lastTime;

      // Update scroll direction
      if (deltaY > 0) {
        globalScrollDirection = 1; // Scrolling down
      } else if (deltaY < 0) {
        globalScrollDirection = -1; // Scrolling up
      }

      // Calculate velocity in pixels per second (framerate-independent)
      // Avoid division by zero
      if (deltaTime > 0) {
        targetVelocity = (Math.abs(deltaY) / deltaTime) * 1000; // Convert to pixels/second
      }

      lastScrollY = currentScrollY;
      lastTime = currentTime;
    },
  });

  // Smoothly lerp current velocity to target velocity every frame
  gsap.ticker.add(() => {
    // Lerp factor: lower = smoother (0.08 for buttery smooth)
    const lerpFactor = 0.08;

    // Interpolate current velocity towards target
    currentVelocity += (targetVelocity - currentVelocity) * lerpFactor;

    // Decay target velocity (simulates friction) - slower decay for smoothness
    targetVelocity *= 0.95;

    // Snap to zero when very small
    if (Math.abs(currentVelocity) < 0.1) {
      currentVelocity = 0;
    }
    if (Math.abs(targetVelocity) < 0.1) {
      targetVelocity = 0;
    }

    // Update global velocity
    globalScrollVelocity = currentVelocity;
  });

  scrollVelocityInitialized = true;
}

/**
 * Get current scroll velocity
 * @returns Current scroll velocity (pixels per second)
 */
export function getScrollVelocity(): number {
  return globalScrollVelocity;
}

/**
 * Get current scroll direction
 * @returns 1 for down, -1 for up, 0 for not scrolling
 */
export function getScrollDirection(): number {
  return globalScrollDirection;
}

/**
 * Check if GSAP is available
 */
export function hasGsap(): boolean {
  return typeof window.gsap !== 'undefined';
}

/**
 * Check if ScrollTrigger is available
 */
export function hasScrollTrigger(): boolean {
  return typeof window.ScrollTrigger !== 'undefined';
}

/**
 * Check if Draggable is available
 */
export function hasDraggable(): boolean {
  return typeof window.Draggable !== 'undefined';
}

/**
 * Get GSAP instance with type safety
 */
export function getGsap(
  moduleName?: string,
  required: boolean = true
): { gsap: GsapInstance; ScrollTrigger?: GsapPlugin; Draggable?: GsapDraggable } | null {
  if (!hasGsap()) {
    if (required && !hasWarned) {
      console.warn(
        '[gsap] GSAP library not detected. Some animations will be skipped. ' +
          'Add GSAP to your project: https://greensock.com/docs/v3/Installation'
      );
      hasWarned = true;
    } else if (moduleName && !hasWarned) {
      console.warn(`[${moduleName}] GSAP not available, module will use fallback behavior`);
      hasWarned = true;
    }
    return null;
  }

  return {
    gsap: window.gsap!,
    ScrollTrigger: window.ScrollTrigger,
    Draggable: window.Draggable,
  };
}

/**
 * Warn about missing GSAP dependencies
 * Call this from modules to alert developers about missing plugins
 *
 * @param moduleName - Name of the module requiring the dependency
 * @param dependencies - Array of dependency names (e.g., ['ScrollTrigger', 'Draggable'])
 * @param severity - 'error' for required dependencies, 'warn' for optional
 *
 * @example
 * // Required dependency (module won't work without it)
 * warnMissingDependencies('marquee', ['ScrollTrigger'], 'error');
 *
 * @example
 * // Optional dependency (module has fallback)
 * warnMissingDependencies('counter', ['ScrollTrigger'], 'warn');
 */
export function warnMissingDependencies(
  moduleName: string,
  dependencies: Array<'ScrollTrigger' | 'Draggable'>,
  severity: 'error' | 'warn' = 'warn'
): void {
  const missing: string[] = [];

  dependencies.forEach((dep) => {
    if (dep === 'ScrollTrigger' && !hasScrollTrigger()) {
      missing.push('ScrollTrigger');
    } else if (dep === 'Draggable' && !hasDraggable()) {
      missing.push('Draggable');
    }
  });

  if (missing.length === 0) return;

  const pluginList = missing.join(', ');
  const message =
    severity === 'error'
      ? `[${moduleName}] Missing required GSAP plugin(s): ${pluginList}. Module will not work correctly. Add to your project: https://greensock.com/docs/v3/Installation`
      : `[${moduleName}] Missing optional GSAP plugin(s): ${pluginList}. Some features will be disabled. Add for full functionality: https://greensock.com/docs/v3/Installation`;

  if (severity === 'error') {
    console.error(message);
  } else {
    console.warn(message);
  }
}

/**
 * Reset warning state (useful for testing)
 */
export function resetGsapWarning(): void {
  hasWarned = false;
}

/**
 * Initialize ScrollTrigger refresh on image load
 */
export function initScrollTriggerRefresh(): void {
  if (scrollTriggerRefreshInitialized || !hasScrollTrigger()) {
    return;
  }

  let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
  const refreshDelay = 50; // Debounce delay in ms

  // Debounced refresh function
  const debouncedRefresh = () => {
    if (refreshTimeout !== null) {
      clearTimeout(refreshTimeout);
    }
    refreshTimeout = setTimeout(() => {
      window.ScrollTrigger!.refresh();
      refreshTimeout = null;
    }, refreshDelay);
  };

  // Listen for all image load events using event delegation
  document.addEventListener(
    'load',
    (e) => {
      if (e.target instanceof HTMLImageElement) {
        debouncedRefresh();
      }
    },
    true
  ); // Use capture phase to catch all image loads

  scrollTriggerRefreshInitialized = true;
}

/**
 * Check if user prefers reduced motion
 * @returns true if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Initialize scroll-based animations after IX3 loads
 * Triggers ScrollTrigger by performing a mock scroll (1px down, then back)
 * Waits indefinitely for IX3 - this runs independently without blocking other initialization
 */
export async function initScrollAnimations(): Promise<void> {
  // Wait for IX3 to load (waits indefinitely, no timeout)
  await waitForIx3();

  // Trigger scroll-based animations (only if page is scrollable)
  if (hasGsap() && document.body.scrollHeight > window.innerHeight) {
    const gsap = window.gsap!;
    const currentScroll = window.scrollY;
    gsap.set(window, { scrollTo: currentScroll + 1 });
    gsap.set(window, { scrollTo: currentScroll });
  }
}
